import { Currency } from "@redotech/money/currencies";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import { FulfillmentOrderAddressSchema } from "../fulfillments/fulfillment-order-address";
import {
  FreightChargeCategory,
  FreightClass,
  FreightLocationType,
  FreightServiceLevel,
  HandlingUnitType,
  KnownSCAC,
} from "./freight";

const PickupAccessorialsSchema_ = z.object({
  liftgateRequired: z.boolean(),
  insidePickup: z.boolean(),
  instructions: z.string().nullish(),
});
export const PickupAccessorialsSchema: z.ZodType<PickupAccessorials> =
  PickupAccessorialsSchema_;
export interface PickupAccessorials extends z.infer<
  typeof PickupAccessorialsSchema_
> {}

const DeliveryAccessorialsSchema_ = z.object({
  liftgateRequired: z.boolean(),
  insideDelivery: z.boolean(),
  appointmentRequired: z.boolean(),
  instructions: z.string().nullish(),
});
export const DeliveryAccessorialsSchema: z.ZodType<DeliveryAccessorials> =
  DeliveryAccessorialsSchema_;
export interface DeliveryAccessorials extends z.infer<
  typeof DeliveryAccessorialsSchema_
> {}

const FreightDimensionsSchema_ = z.object({
  length: z.number(),
  width: z.number(),
  height: z.number(),
});
export const FreightDimensionsSchema: z.ZodType<FreightDimensions> =
  FreightDimensionsSchema_;
export interface FreightDimensions extends z.infer<
  typeof FreightDimensionsSchema_
> {}

const FreightShippedItemSchema_ = z.object({
  description: z.string(),
  quantity: z.number(),
  weightLbs: z.number(),
  dimensions: FreightDimensionsSchema.nullish(),
});
export const FreightShippedItemSchema: z.ZodType<FreightShippedItem> =
  FreightShippedItemSchema_;
export interface FreightShippedItem extends z.infer<
  typeof FreightShippedItemSchema_
> {}

const FreightHandlingUnitSchema_ = z.object({
  type: createEnum(HandlingUnitType, { enumName: "HandlingUnitType" }),
  quantity: z.number(),
  stackable: z.boolean(),
  freightClass: createEnum(FreightClass, { enumName: "FreightClass" }),
  totalWeightLbs: z.number(),
  dimensions: FreightDimensionsSchema.nullish(),
  shippedItems: z.array(FreightShippedItemSchema),
});
export const FreightHandlingUnitSchema: z.ZodType<FreightHandlingUnit> =
  FreightHandlingUnitSchema_;
export interface FreightHandlingUnit extends z.infer<
  typeof FreightHandlingUnitSchema_
> {}

const FreightQuoteRequestSchema_ = z.object({
  originAddress: FulfillmentOrderAddressSchema,
  destinationAddress: FulfillmentOrderAddressSchema,
  pickupDateTime: z.string(),
  pickupLocationType: createEnum(FreightLocationType, {
    enumName: "FreightLocationType",
  }).nullish(),
  deliveryLocationType: createEnum(FreightLocationType, {
    enumName: "FreightLocationType",
  }).nullish(),
  pickupAccessorials: PickupAccessorialsSchema,
  deliveryAccessorials: DeliveryAccessorialsSchema,
  handlingUnitList: z.array(FreightHandlingUnitSchema),
  customerReference1: z.string().nullish(),
  customerReference2: z.string().nullish(),
  specialInstructions: z.string().nullish(),
});
export const FreightQuoteRequestSchema: z.ZodType<FreightQuoteRequest> =
  FreightQuoteRequestSchema_;
export interface FreightQuoteRequest extends z.infer<
  typeof FreightQuoteRequestSchema_
> {}

const FreightQuoteSchema_ = z.object({
  quoteId: z.string(),
  productTransactionId: z.string(),
  scac: createEnum(KnownSCAC, { enumName: "KnownSCAC" }),
  serviceLevel: createEnum(FreightServiceLevel, {
    enumName: "FreightServiceLevel",
  }),
  estDeliveryDays: z.number().nullish(),
  estDeliveryDate: z.string().nullish(),
  quoteExpirationDate: z.string().nullish(),
  providerCost: z.string(),
  totalPrice: z.object({
    value: z.number(),
    unit: createEnum(Currency, { enumName: "Currency" }),
  }),
  priceBreakdown: z.array(
    z.object({
      price: z.object({
        value: z.number(),
        unit: createEnum(Currency, { enumName: "Currency" }),
      }),
      category: createEnum(FreightChargeCategory, {
        enumName: "FreightChargeCategory",
      }),
      description: z.string(),
    }),
  ),
});

export const FreightQuoteSchema: z.ZodType<FreightQuote> = FreightQuoteSchema_;
export interface FreightQuote extends z.infer<typeof FreightQuoteSchema_> {}
